import { cn } from "../../design-system/components";
import { useText } from "../../clients/words";
import { vidDostavki, type FaktDostavki, type GolosLenty, type PodpisStrokiLenty, type VlozhenieLenty } from "../api";
import { Vlozhenie } from "./Vlozhenie";
import { ZnachokDostavki } from "./ZnachokDostavki";
import { ZnachokKanala } from "./ZnachokKanala";

/**
 * ПУЗЫРЬ СООБЩЕНИЯ — ОДИН НА «ПЕРЕПИСКИ», КАРТОЧКУ ЛИДА И КАРТОЧКУ ЗАПИСИ (`s713`).
 *
 * Слова задания (приёмка штаба 05.10.2026): «база визуального стандарта — существующий
 * раздел „Переписки“. Один визуальный чат в лидах, записях и переписках». До этого диалог
 * («Этот диалог») и сквозная лента клиентки («Вся хроника», она же переписка карточки
 * лида) рисовали пузырь каждый по-своему: в ленте не было ни галочки доставки, ни пометки
 * шаблона, ни причины «не ушло». Теперь пузырь один, и правка вида — в одном месте (§31.8).
 *
 * ⛔ ПОДПИСЬ — ИЗ САМОЙ СТРОКИ (Д-02/04/16): входящее — клиентка или её аккаунт и
 * «Получено на <наша линия>»; исходящее — сотрудник или робот и «Отправлено с <наша линия>».
 * Линию присылает сервер по каналу ЭТОГО сообщения, экран её не выбирает и не подставляет
 * выбранную сейчас линию ответа.
 */

export interface StrokaPuzyrya {
  nashe: boolean;
  tekst: string;
  vremya: string;
  dostavka: string | null;
  dostavka_prichina?: string | null;
  dostavka_fakt?: FaktDostavki | null;
  po_shablonu: string | null;
  vlozhenie: VlozhenieLenty | null;
  golos: GolosLenty | null;
  podpis?: PodpisStrokiLenty | null;
}

export function PodpisStroki({
  podpis,
  nashe,
  kanalKind,
  color,
}: {
  podpis: PodpisStrokiLenty | null | undefined;
  nashe: boolean;
  /** Сквозная лента идёт по нескольким мессенджерам — там у подписи значок канала строки. */
  kanalKind?: string;
  color?: string;
}) {
  if (podpis == null) return null;

  return (
    <span
      data-testid="podpis-stroki"
      data-nashe={nashe ? "da" : "net"}
      // ⛔ Не через `cn`: слияние классов принимает размер `text-ops-micro` и цвет
      // `text-ops-text-tertiary` за один и тот же вид и выбрасывает размер — подпись выходила
      // крупнее самого сообщения (поймано глазом на стенде).
      className={`mt-2 flex max-w-[92%] flex-wrap items-center gap-x-1.5 text-ops-micro text-ops-text-tertiary ${
        nashe ? "justify-end self-end text-right" : "self-start"
      }`}
    >
      {kanalKind !== undefined && kanalKind !== "" && (
        <ZnachokKanala kind={kanalKind} color={color ?? "var(--ops-primary, #7c3aed)"} razmer={14} />
      )}
      <span className="font-medium text-ops-text-secondary" data-testid="podpis-kto">
        {podpis.kto}
      </span>
      {/* `s720`: над входящим — аккаунт клиентки в мессенджере, отдельно от имени и от нашей линии. */}
      {!nashe && podpis.akkaunt != null && podpis.akkaunt !== "" && (
        <span data-testid="podpis-akkaunt">{podpis.akkaunt}</span>
      )}
      {podpis.liniya !== null && podpis.liniya !== "" && (
        <span data-testid="podpis-liniya">
          {podpis.liniya_slovo} {podpis.liniya}
        </span>
      )}
    </span>
  );
}

export function Puzyr({
  s,
  onRasshifrovat,
  zanyat,
  posleSvoeyPodpisi = false,
}: {
  s: StrokaPuzyrya;
  onRasshifrovat: () => void;
  zanyat: boolean;
  /** Над пузырём стоит его подпись — отступ сверху уже дала она. */
  posleSvoeyPodpisi?: boolean;
}) {
  const text = useText();
  // Вложение без слов приезжает с текстом-названием вида («Фото») — второй раз его не пишем.
  const tekstVidno = s.tekst !== "" && !(s.vlozhenie !== null && s.tekst === s.vlozhenie.vid_display);
  /*
   * ⛔ `s713` по контракту `s711` (Д-18): значок — от ФАКТА доставки. Прежний вид рисовал
   * «отправлено» двумя галочками у каналов, которые о доставке не сообщают (Telegram-бот,
   * ВК, MAX) — на экране оно читалось как «доставлено». Что известно на самом деле,
   * сервер говорит словом; оно стоит подсказкой у значка.
   */
  const vid = vidDostavki(s);
  const prichina = s.dostavka_fakt?.oshibka?.slovo ?? s.dostavka_prichina ?? null;

  return (
    <>
      {/*
        ⛔⛔ РАССЫЛКА ПО ШАБЛОНУ — ОТДЕЛЬНОЙ ВЕЛИЧИНОЙ НАД ПУЗЫРЁМ, А НЕ ВКЛЕЙКОЙ В ТЕЛО
        (`s610`, находка s609): пометка отвечает на вопрос «кто это написал — человек или
        рассылка» (владелец, п.9: «где сообщение, где переписка где смс, где шаблон от
        триггера рассылки где что»). Внутри слов она была бы неотличима от сказанного.
      */}
      {s.po_shablonu !== null && (
        <span
          className={cn(
            "mb-[-1px] block w-fit max-w-[82%] rounded-t-ops-lg bg-ops-surface-2 px-3 pt-1 pb-1.5 text-ops-micro uppercase tracking-wide text-ops-text-tertiary",
            posleSvoeyPodpisi ? "mt-0.5" : "mt-1.5",
            s.nashe ? "self-end" : "self-start",
          )}
        >
          {s.po_shablonu}
        </span>
      )}
      <div
        data-testid="puzyr"
        className={cn(
          "max-w-[82%] rounded-ops-lg px-3 py-2 text-ops-caption leading-snug",
          s.po_shablonu !== null && "mt-0 rounded-t-none",
          s.po_shablonu === null && (posleSvoeyPodpisi ? "mt-0.5" : "mt-1.5"),
          /*
            ⛔ ЦВЕТА ПУЗЫРЕЙ — С МАКЕТА (Астра, 16.09; владелец: «то что я вижу не совпадает
            с дизайном астры абсолюнто»). Своё сообщение голубое, сообщение клиентки белое
            с тонкой рамкой.
          */
          s.nashe
            ? "self-end bg-ops-puzyr-svoy text-ops-text"
            : "self-start border border-ops-border bg-ops-puzyr-chuzhoy text-ops-text",
        )}
      >
        {s.vlozhenie !== null && (
          <Vlozhenie v={s.vlozhenie} golos={s.golos} onRasshifrovat={onRasshifrovat} zanyat={zanyat} />
        )}
        {tekstVidno && s.po_shablonu === null && (
          <span className="whitespace-pre-wrap break-words">{s.tekst}</span>
        )}
        {/* Шаблон с кнопками — кнопками, как у клиентки, а не «| [Принять]». */}
        {tekstVidno && s.po_shablonu !== null && <TekstShablona tekst={s.tekst} />}
        <span className="mt-1 flex items-center justify-end gap-1 text-ops-micro text-ops-text-tertiary">
          {s.vremya}
          {/*
            ⛔⛔ ГАЛОЧКА — ТОЛЬКО ПО ДОСТАВКЕ, А НЕ ПО «ПРИНЯТО К ОТПРАВКЕ» (`s604`), И
            ЛЕСТНИЦА ЦЕЛИКОМ ОДНИМ КОМПОНЕНТОМ (`s608`): сервер присылает готовый вид,
            рисует его один компонент на список и на ленту.
          */}
          {s.nashe && (
            <span className="inline-flex" title={s.dostavka_fakt?.slovo ?? undefined} data-testid="znachok-dostavki" data-vid={vid ?? ""}>
              <ZnachokDostavki vid={vid} />
            </span>
          )}
          {s.nashe && vid === "ne_ushlo" && (
            <span className="text-ops-danger">{text("obrashcheniya.ne_ushlo")}</span>
          )}
          {/*
            `s720` (приёмка 06.10.2026: «отображать фактическую доставку, ошибку и неизвестный
            исход»). Одна галочка без слов читалась как «всё в порядке». Когда мессенджер принял
            сообщение, а дошло ли — узнать нечем (канал о доставке не сообщает, подтверждения нет
            больше суток), это сказано словами рядом с галочкой; полная фраза сервера — в подсказке.
          */}
          {s.nashe && vid !== "ne_ushlo" && s.dostavka_fakt?.dostavka === "neizvestno" && (
            <span data-testid="dostavka-neizvestna" title={s.dostavka_fakt.slovo ?? undefined}>
              {text("otpravka.dostavka_neizvestna")}
            </span>
          )}
        </span>
        {/*
          ⛔ ПОЧЕМУ НЕ УШЛО — словами, под сообщением (владелец 24.09: «если на той стороне
          нет вацапа, то писать прямо ошибку почему не ушло»). Фразу собирает сервер.
        */}
        {s.nashe && vid === "ne_ushlo" && prichina && (
          <span className="mt-1 block whitespace-pre-wrap break-words text-ops-danger">
            {prichina}
          </span>
        )}
      </div>
    </>
  );
}

/**
 * Кнопки согласованного шаблона приезжают хвостом текста в записи провайдера:
 * `… | [Оставить отзыв,https://…]` или `… | [Принять]`. Клиентка видит их
 * кнопками, а не скобками, — так их и показываем: подпись без адреса.
 */
function razobratKnopkiShablona(tekst: string): [string, string[]] {
  const chasti = tekst.split(/\s*\|\s*(?=\[)/);
  if (chasti.length < 2) return [tekst, []];

  const knopki = chasti
    .slice(1)
    .flatMap((ch) => [...ch.matchAll(/\[([^\]]+)\]/g)].map((m) => m[1].split(",")[0].trim()))
    .filter((k) => k !== "");

  return [chasti[0], knopki];
}

/** Текст шаблона и его кнопки — одно место на просмотр и на ленту (§31.8). */
export function TekstShablona({ tekst }: { tekst: string }) {
  const [telo, knopki] = razobratKnopkiShablona(tekst);

  return (
    <>
      <span className="whitespace-pre-wrap break-words">{telo}</span>
      {knopki.length > 0 && (
        <span className="mt-2 flex flex-wrap gap-1.5">
          {knopki.map((k) => (
            <span
              key={k}
              className="rounded-ops-md border border-ops-border bg-ops-surface px-2 py-1 text-ops-caption text-ops-primary"
            >
              {k}
            </span>
          ))}
        </span>
      )}
    </>
  );
}
